import {
  formatDuration,
  formatWithSpecificUnits,
  formatBusinessDuration,
} from "./duration";
import { filesize } from "humanize";
import { formatCalendarValue, formatDatePart } from "./date";
import { extractCleanFieldName, FieldParts } from "./strings";

export type ValueType =
  | "count"
  | "sum"
  | "average"
  | "min"
  | "max"
  | "duration"
  | "businessDuration"
  | "datepart"
  | "percentage"
  | "bytes"
  | "currency"
  | "date"
  | "ratio"
  | "sentiment"
  | "custom"
  | "dayofweek"
  | "string";

// Map of value types to their default units
export const DEFAULT_UNITS: Record<ValueType, string> = {
  duration: "millisecond",
  businessDuration: "second",
  bytes: "byte",
  currency: "USD",
  percentage: "percent",
  ratio: "ratio",
  date: "date",
  datepart: "day",
  dayofweek: "day",
  sentiment: "sentiment",
  count: "",
  sum: "",
  average: "",
  min: "",
  max: "",
  custom: "",
  string: "",
};

// Default format options for all values
export const DEFAULT_FORMAT_OPTIONS: FormatOptions = {
  style: "human",
  precision: 1,
  type: "custom",
};

// Valid units for each value type
export const VALID_UNITS: Record<ValueType, string[]> = {
  duration: [
    "millisecond",
    "milliseconds",
    "second",
    "seconds",
    "minute",
    "minutes",
    "hour",
    "hours",
    "day",
    "days",
    "week",
    "weeks",
    "month",
    "months",
    "year",
    "years",
  ],
  businessDuration: ["second"],
  bytes: ["byte", "kilobyte", "megabyte", "gigabyte"],
  currency: ["USD", "EUR", "GBP", "JPY"],
  percentage: ["percent", "%"],
  ratio: ["ratio"],
  date: ["date"],
  datepart: [
    "year",
    "month",
    "day",
    "hour",
    "minute",
    "second",
    "quarter",
    "week",
  ],
  dayofweek: ["day"],
  sentiment: ["sentiment"],
  count: [],
  sum: [],
  average: [],
  min: [],
  max: [],
  custom: [],
  string: [],
};

export interface FormatOptions {
  type?: ValueType;
  style?: "compact" | "long" | "human";
  field?: string;
  unit?: string;
  precision?: number;
  currency?: string;
  datePartFormat?: string; // For datepart type (e.g., 'month', 'quarter', etc.)
  customSuffix?: string; // For custom unit type
  toUnit?: string; // Target unit for conversions
}

type unitQuantity = { qty: number; label: string; selectable?: boolean };

const timeQuantities: Record<string, unitQuantity> = {
  millisecond: { qty: 1, label: "ms" },
  second: { qty: 1000, label: "s" },
  minute: { qty: 60_000, label: "min", selectable: true },
  hour: { qty: 3_600_000, label: "hr", selectable: true },
  day: { qty: 3_600_000 * 24, label: "day", selectable: true },
  week: { qty: 3_600_000 * 24 * 7, label: "wk", selectable: true },
};

const byteQuantities: Record<string, unitQuantity> = {
  kilobyte: { qty: 1024, label: "KB", selectable: true },
  megabyte: { qty: 1024 * 1024, label: "MB", selectable: true },
  gigabyte: { qty: 1024 * 1024 * 1024, label: "GB", selectable: true },
};

export const relativeUnitsFor = (unit: string) => {
  const realm = [timeQuantities, byteQuantities].find((r) => r[unit]);
  if (realm == null) {
    return null;
  }
  const qty = realm[unit].qty;
  const options = Object.values(realm).filter((v) => v.selectable);
  return { qty, options };
};

export function extractFieldParts(field: string): FieldParts {
  const fieldLower = field.toLowerCase();

  // Extract function if present (e.g. "avg" from "avg(issue.cycletime)")
  const functionMatch = fieldLower.match(/^([A-Za-z]+)\((.*?)\)$/);
  const func = functionMatch?.[1];
  const remaining = functionMatch?.[2] || fieldLower;

  // Split remaining parts by dot
  const parts = remaining.split(".");

  // Return structured parts
  return {
    function: func,
    target: parts.length > 1 ? parts[0] : undefined,
    attribute: parts.at(-1) ?? "",
  };
}

export class ValueFormatter {
  private static readonly DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Type detection utilities
  public static detectValueType(options?: FormatOptions): ValueType | null {
    // First check if type is explicitly provided in options
    if (options?.type) {
      return options.type;
    }

    // Fall back to field name detection if we have a field name
    if (!options?.field) return null;

    const cleanField = extractCleanFieldName(options.field);
    // Detection based on field name keywords
    if (
      cleanField.includes("date") ||
      cleanField.includes("timestamp") ||
      cleanField.includes("datetime")
    ) {
      return "date";
    }

    if (
      cleanField.includes("time") ||
      cleanField.includes("duration") ||
      cleanField.includes("workday") ||
      cleanField.includes("timeperiod") ||
      cleanField.includes("worktime")
    ) {
      return "duration";
    }

    if (cleanField.includes("dayofweek") || cleanField.includes("weekday")) {
      return "dayofweek";
    }

    if (cleanField.includes("percent")) {
      return "percentage";
    }

    if (cleanField.includes("size") || cleanField.includes("bytes")) {
      return "bytes";
    }

    if (cleanField.includes("cost") || cleanField.includes("price")) {
      return "currency";
    }

    if (cleanField.includes("sentiment")) {
      return "sentiment";
    }

    return "string";
  }

  /**
   * Detect the measurement unit of the field value
   * This determines what the raw numeric value represents (seconds, bytes, etc.)
   * Essential for proper conversion and formatting
   */
  public static detectValueUnit(options?: FormatOptions): string | null {
    // If unit is explicitly provided, use it
    if (options?.unit) {
      return options.unit;
    }

    // If no field name, can't infer unit
    if (!options?.field) return null;

    const cleanField = extractCleanFieldName(options.field);

    // First determine what the value type is
    const valueType = this.detectValueType(options);

    // For business time fields - check these first to avoid confusion with other time units
    if (
      cleanField.includes("worktime") ||
      cleanField.includes("businesstime") ||
      cleanField.includes("businessduration")
    ) {
      return "second"; // Business time is typically measured in seconds
    }

    // Look for specific unit indicators in the field name
    // Key pattern: [field name]In[Unit] - should match patterns like timeInSeconds, not worktimesincelastcommit
    const inUnitMatch =
      cleanField.match(/\bin([a-z]+)s?$/i) ||
      cleanField.match(/\bin([a-z]+)s?\b/i);
    if (inUnitMatch) {
      const unitHint = inUnitMatch[1].toLowerCase();

      // Validate the extracted unit to make sure it's reasonable
      const validUnitPattern =
        /^(milli|sec|min|hour|day|week|month|year|byte|kb|mb|gb|percent)s?$/i;
      if (validUnitPattern.test(unitHint)) {
        // Map common unit terms to our standard units
        if (/milli(second)?/.test(unitHint)) return "millisecond";
        if (/sec(ond)?/.test(unitHint)) return "second";
        if (/min(ute)?/.test(unitHint)) return "minute";
        if (/hour|hr/.test(unitHint)) return "hour";
        if (/day/.test(unitHint) && !/week/.test(unitHint)) return "day";
        if (/week/.test(unitHint)) return "week";
        if (/month/.test(unitHint)) return "month";
        if (/year/.test(unitHint)) return "year";

        if (/byte/.test(unitHint)) return "byte";
        if (/kb|kilobyte/.test(unitHint)) return "kilobyte";
        if (/mb|megabyte/.test(unitHint)) return "megabyte";
        if (/gb|gigabyte/.test(unitHint)) return "gigabyte";

        // Return as-is if we can't map it but it's still valid
        return unitHint;
      }
      // If it's not a valid unit, continue to check other patterns
    }

    // Check for prefixes/suffixes indicating units
    if (cleanField.endsWith("ms") || cleanField.includes("millisec"))
      return "millisecond";
    if (cleanField.endsWith("secs") || cleanField.endsWith("seconds"))
      return "second";
    if (cleanField.endsWith("mins") || cleanField.endsWith("minutes"))
      return "minute";
    if (cleanField.endsWith("hrs") || cleanField.endsWith("hours"))
      return "hour";
    if (cleanField.endsWith("days") && !cleanField.includes("week"))
      return "day";
    if (cleanField.endsWith("weeks")) return "week";
    if (cleanField.endsWith("months")) return "month";
    if (cleanField.endsWith("years")) return "year";

    if (cleanField.endsWith("bytes")) return "byte";
    if (cleanField.endsWith("kb")) return "kilobyte";
    if (cleanField.endsWith("mb")) return "megabyte";
    if (cleanField.endsWith("gb")) return "gigabyte";

    // Check for unit indicators embedded in field names
    if (cleanField.includes("millisec") || cleanField.includes("_ms_"))
      return "millisecond";
    if (cleanField.includes("_sec_") || cleanField.includes("_second_"))
      return "second";
    if (cleanField.includes("_min_") || cleanField.includes("_minute_"))
      return "minute";
    if (cleanField.includes("_hr_") || cleanField.includes("_hour_"))
      return "hour";
    if (cleanField.includes("_day_") && !cleanField.includes("_week_"))
      return "day";

    // If we have a value type, use its default unit
    if (valueType && DEFAULT_UNITS[valueType]) {
      return DEFAULT_UNITS[valueType];
    }

    return null;
  }

  /**
   * Main formatting method that handles all value types
   * @param value The numeric value to format
   * @param options Formatting options including type and unit
   * @returns Formatted string representation
   */
  static format(value: number, options: FormatOptions): string {
    const num = Number(value);
    if (value === null || Number.isNaN(num) || !Number.isFinite(num)) {
      return "-";
    }

    // Determine the value type (what kind of data)
    const valueType = this.detectValueType(options) || "custom";

    // Determine the input unit (what the value is measured in)
    const unit = options.unit || this.detectValueUnit(options);

    // Create enhanced options with detected values
    const enhancedOptions: FormatOptions = {
      ...DEFAULT_FORMAT_OPTIONS,
      type: valueType,
      unit: unit || undefined, // Convert null to undefined
      toUnit: options.toUnit || undefined,
    };

    // Handle different value types with appropriate formatting
    switch (valueType) {
      case "duration":
      case "businessDuration": {
        // For durations, unit tells us what the value is measured in (seconds, minutes, etc.)
        return formatDuration(value, enhancedOptions);
      }

      case "percentage":
      case "ratio": {
        return this.formatPercentage(value, valueType);
      }

      case "bytes": {
        // For bytes, unit tells us if it's bytes, KB, MB, etc.
        if (unit && unit !== "byte") {
          // Convert to bytes for filesize formatter
          const valueInBytes = this.convertToBaseUnit(value, unit, "byte");
          return filesize(valueInBytes);
        }
        return filesize(value);
      }

      case "currency": {
        // For currency, unit is the currency code
        return this.formatCurrency(
          value,
          enhancedOptions.currency ||
            (unit && VALID_UNITS[valueType].includes(unit) ? unit : undefined)
        );
      }

      case "date": {
        return new Date(value).toLocaleDateString();
      }

      case "datepart": {
        return this.formatDatePart(value, enhancedOptions.datePartFormat);
      }

      case "sentiment": {
        return this.formatSentiment(value);
      }

      case "custom": {
        return `${value.toLocaleString()}${enhancedOptions.customSuffix || ""}`;
      }

      case "count": {
        return this.formatLargeNumber(value);
      }

      case "sum":
      case "average": {
        return this.formatSumOrAverage(value, valueType, enhancedOptions);
      }

      case "min":
      case "max": {
        return this.formatMinOrMax(value, enhancedOptions);
      }

      case "dayofweek": {
        return this.formatDayOfWeek(value);
      }

      default: {
        return value?.toLocaleString() || "";
      }
    }
  }

  // Format with unit - handles unit-specific formatting when unit is directly provided
  static formatWithUnit(
    value: number,
    unit: string | undefined,
    toUnit?: string
  ): string {
    const formattedValue = this.formatWithUnitArray(value, unit, toUnit);
    if (formattedValue.length === 1) {
      return formattedValue[0];
    }
    return formattedValue.join(" ");
  }

  static formatWithUnitArray(
    value: number,
    unit: string | undefined,
    toUnit?: string
  ): string[] {
    // Handle missing unit or NaN values
    if (!unit) {
      return [value.toString()];
    }
    if (Number.isNaN(value)) {
      return ["-"];
    }

    // Determine the value type from the unit
    let valueType: ValueType | null = null;
    for (const [type, units] of Object.entries(VALID_UNITS)) {
      if (units.includes(unit)) {
        valueType = type as ValueType;
        break;
      }
    }

    // Use the format method with the detected type and explicitly provided unit
    if (valueType) {
      return [this.format(value, { type: valueType, unit, toUnit })];
    }

    // Special cases for complex unit strings
    if (unit.startsWith("datepart-")) {
      const format = unit.slice(9);
      return [formatDatePart(value, format)];
    }

    if (["yearmonthday", "yearweek"].includes(unit)) {
      return [formatCalendarValue(value, unit)];
    }

    // Handle special case for businesssecond which isn't in our standard units
    if (unit === "businesssecond") {
      return [
        formatDuration(value, {
          unit: "second",
          toUnit,
          type: "businessDuration",
        }),
      ];
    }
    return [`${value.toFixed(1)}`, `${unit}`];
  }

  private static formatPercentage(value: number, type: ValueType): string {
    if (value >= 0 && value <= 1) {
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 1,
      }).format(value);
    }

    return type === "percentage"
      ? `${(value * 100).toFixed(1)}%`
      : value.toFixed(1);
  }

  private static formatCurrency(value: number, currency?: string): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  }

  private static formatDatePart(value: number, format?: string): string {
    if (!format) return value.toString();
    return formatDatePart(value, format);
  }

  private static formatSentiment(value: number): string {
    if (value <= -0.25) return "negative";
    if (value >= 0.25) return "positive";
    return "neutral";
  }

  private static formatSumOrAverage(
    value: number,
    type: ValueType,
    options: FormatOptions
  ): string {
    const precision = options.precision ?? 2;

    if (options.customSuffix) {
      const formattedValue =
        type === "sum"
          ? this.formatLargeNumber(value)
          : value.toFixed(Math.min(precision, 1));
      return `${formattedValue}${options.customSuffix}`;
    }

    return type === "sum"
      ? this.formatLargeNumber(value)
      : value.toFixed(Math.min(precision, 1));
  }

  private static formatMinOrMax(value: number, options: FormatOptions): string {
    if (options.customSuffix) {
      return `${value.toLocaleString()}${options.customSuffix}`;
    }
    return value.toLocaleString();
  }

  private static formatDayOfWeek(value: number): string {
    const dayIndex = Math.floor(value) % 7;
    return this.DAYS_OF_WEEK[dayIndex];
  }

  private static formatLargeNumber(num: number): string {
    if (num < 1000) return num.toString();
    const units = ["", "k", "M", "B", "T"];
    const order = Math.floor(Math.log10(num) / 3);
    const unitName = units[order];
    const value = num / Math.pow(1000, order);
    return `${value.toFixed(1)}${unitName}`;
  }

  /**
   * Helper to convert between units of the same type
   * @param value The value to convert
   * @param fromUnit The unit the value is currently in
   * @param toUnit The unit to convert to
   * @returns The converted value
   */
  private static convertToBaseUnit(
    value: number,
    fromUnit: string,
    toUnit: string
  ): number {
    // Handle time unit conversions
    if (timeQuantities[fromUnit] && timeQuantities[toUnit]) {
      return (
        (value * timeQuantities[fromUnit].qty) / timeQuantities[toUnit].qty
      );
    }

    // Handle byte unit conversions
    if (fromUnit === "kilobyte" && toUnit === "byte") return value * 1024;
    if (fromUnit === "megabyte" && toUnit === "byte")
      return value * 1024 * 1024;
    if (fromUnit === "gigabyte" && toUnit === "byte")
      return value * 1024 * 1024 * 1024;

    // If we don't know how to convert, return the original value
    return value;
  }
}
